import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import { useTenantConfig } from "@/context/TenantConfigContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { roleLabel } from "@/lib/format";
import { TrialBanner } from "@/components/TrialBanner";
import { DemoHelpBanner } from "@/components/DemoHelpBanner";
import { InAppDemoFab } from "@/components/InAppDemoFab";
import { SetupProgressChip } from "@/components/SetupProgressChip";
import { SamplePortfolioExperience } from "@/components/sample/SamplePortfolioExperience";
import { SampleInviteBanner } from "@/components/sample/SampleInviteBanner";
import { QuickAddAccountDialog } from "@/components/QuickAddAccountDialog";
import { ExitSurvey } from "@/components/ExitSurvey";
import QuickStart from "@/components/QuickStart";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  House, Buildings, UsersThree, FolderSimpleUser, ListChecks, Handshake,
  ChatCircleDots, Lightning, CurrencyInr, ChartBar, Scroll, ShieldCheck,
  SignOut, List, X, CreditCard, Broadcast, Lock, Trophy, Gavel, ChartLineUp, UploadSimple, EnvelopeSimple, EnvelopeOpen, UserPlus, Pulse, Sliders, ChartPieSlice, Question, Compass, Broom, WhatsappLogo, PaperPlaneTilt,
} from "@phosphor-icons/react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: House, roles: ["super_admin", "client_admin", "manager", "agent", "auditor"] },
  { to: "/money-stuck", label: "Insights", icon: Pulse, roles: ["super_admin", "client_admin", "manager", "auditor", "agent"] },
  { to: "/connect", label: "Connect", icon: PaperPlaneTilt, roles: ["super_admin", "client_admin", "manager", "agent"] },
  { to: "/tenants", label: "Tenants", icon: Buildings, roles: ["super_admin"] },
  { to: "/billing", label: "Billing", icon: CreditCard, roles: ["super_admin"] },
  { to: "/email-log", label: "Email Log", icon: EnvelopeSimple, roles: ["super_admin"] },
  { to: "/admin/connect-communications", label: "Connect Comms", icon: PaperPlaneTilt, roles: ["super_admin"] },
  { to: "/admin/email-automation", label: "Email Automation", icon: EnvelopeOpen, roles: ["super_admin"] },
  { to: "/leads", label: "Leads", icon: UserPlus, roles: ["super_admin"] },
  { to: "/lead-intel", label: "Sales Intel", icon: ChartLineUp, roles: ["super_admin"] },
  { to: "/subscription", label: "My Plan", icon: CreditCard, roles: ["client_admin", "manager"] },
  { to: "/accounts", label: "Accounts", icon: FolderSimpleUser, roles: ["super_admin", "client_admin", "manager", "agent", "auditor"] },
  { to: "/import", label: "Import Data", icon: UploadSimple, roles: ["super_admin", "client_admin", "manager"] },
  { to: "/work-queue", label: "Work Queue", icon: ListChecks, roles: ["agent", "manager"], module: "work_queue" },
  { to: "/leaderboard", label: "Leaderboard", icon: Trophy, roles: ["super_admin", "client_admin", "manager", "agent", "auditor"], module: "leaderboard" },
  { to: "/settlements", label: "Settlements", icon: Handshake, roles: ["super_admin", "client_admin", "manager", "agent", "auditor"], module: "settlements" },
  { to: "/legal", label: "Legal Cases", icon: Gavel, roles: ["super_admin", "client_admin", "manager", "auditor"], module: "legal" },
  { to: "/communications", label: "Communications", icon: ChatCircleDots, roles: ["super_admin", "client_admin", "manager", "agent"], module: "communications" },
  { to: "/providers", label: "Providers", icon: Broadcast, roles: ["super_admin", "client_admin"], module: "providers", feature: "integrations" },
  { to: "/workflows", label: "Workflows", icon: Lightning, roles: ["super_admin", "client_admin"], module: "workflows", feature: "custom_workflows" },
  { to: "/payments", label: "Payments", icon: CurrencyInr, roles: ["super_admin", "client_admin", "manager", "agent"], module: "payments" },
  { to: "/reports", label: "Reports", icon: ChartBar, roles: ["super_admin", "client_admin", "manager", "auditor"], module: "reports" },
  { to: "/benchmarks", label: "Benchmarks", icon: ChartLineUp, roles: ["super_admin", "client_admin", "manager", "auditor"] },
  { to: "/users", label: "Users", icon: UsersThree, roles: ["super_admin", "client_admin"] },
  { to: "/settings/fields", label: "Field Settings", icon: Sliders, roles: ["super_admin", "client_admin"] },
  { to: "/admin/industry-templates", label: "Industry Templates", icon: Sliders, roles: ["super_admin"] },
  { to: "/admin/meta-events", label: "Meta Events", icon: ChartPieSlice, roles: ["super_admin"] },
  { to: "/admin/whatsapp", label: "WhatsApp", icon: WhatsappLogo, roles: ["super_admin"] },
  { to: "/admin/activation-funnel", label: "Activation Funnel", icon: ChartLineUp, roles: ["super_admin"] },
  { to: "/admin/data-hygiene", label: "Data Hygiene", icon: Broom, roles: ["super_admin"] },
  { to: "/audit", label: "Audit Log", icon: Scroll, roles: ["super_admin", "client_admin", "manager", "auditor"], feature: "audit_logs" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const logoutAll = async () => {
    try { await api.post("/auth/logout-all"); } catch (e) { /* session may already be gone */ }
    toast.success("Signed out from all devices");
    logout();
  };
  const { isSuper, tenants, activeTenant, selectTenant } = useTenant();
  const { terms, hasModule, branding } = useTenantConfig();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const items = NAV
    .filter((n) => n.roles.includes(user?.role) && (!n.module || hasModule(n.module)))
    .map((n) => (n.to === "/accounts" ? { ...n, label: terms.nav_accounts } : n));
  const [urgent, setUrgent] = useState(0);
  const [features, setFeatures] = useState(null);
  const [quickAdd, setQuickAdd] = useState({ open: false, source: "sample", prefill: null });
  useEffect(() => {
    const onQuickAdd = (e) => setQuickAdd({ open: true, source: e?.detail?.source || "sample", prefill: e?.detail?.prefill || null });
    window.addEventListener("recoveros:quick-add", onQuickAdd);
    return () => window.removeEventListener("recoveros:quick-add", onQuickAdd);
  }, []);
  const hasQueue = ["agent", "manager"].includes(user?.role);
  useEffect(() => {
    if (hasQueue) api.get("/work-queue/count").then((r) => setUrgent(r.data.urgent || 0)).catch(() => {});
  }, [hasQueue]);
  useEffect(() => {
    if (user?.role && user.role !== "super_admin") {
      api.get("/me/subscription").then((r) => setFeatures(r.data?.features || [])).catch(() => setFeatures([]));
    }
  }, [user?.role]);
  const isLocked = (n) => n.feature && user?.role !== "super_admin" && features !== null && !features.includes(n.feature);

  return (
    <div className="min-h-screen flex bg-secondary/40">
      {/* Sidebar */}
      <aside
        className={`fixed lg:static z-40 inset-y-0 left-0 w-60 bg-foreground text-background flex flex-col transition-transform ${
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        data-testid="sidebar"
      >
        <div className="h-16 flex items-center gap-2 px-5 border-b border-white/10">
          {branding.logo ? (
            <img src={branding.logo} alt="" className="h-9 w-9 rounded-sm object-cover bg-white/10" data-testid="brand-logo" />
          ) : (
            <img src="/recoveros-icon.png" alt="RecoverOS" className="h-9 w-9 object-contain" data-testid="brand-logo" />
          )}
          <div className="min-w-0">
            <div className="font-heading font-black text-lg leading-none tracking-tight truncate" data-testid="brand-name">{branding.product_name || "RecoverOS"}</div>
            <div className="text-[10px] tracking-[0.2em] text-white/50 uppercase truncate">{branding.company_name || "Trustera"}</div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-3">
          {items.map((n) => {
            const Icon = n.icon;
            if (isLocked(n)) {
              return (
                <button
                  key={n.to}
                  onClick={() => { setOpen(false); navigate("/subscription"); }}
                  data-testid={`nav-locked-${n.label.toLowerCase().replace(/ /g, "-")}`}
                  className="w-full flex items-center gap-3 px-5 py-2.5 text-sm text-white/40 hover:bg-white/5 transition-colors"
                  title="Upgrade your plan to unlock"
                >
                  <Icon size={18} weight="bold" />
                  {n.label}
                  <Lock size={14} weight="bold" className="ml-auto" />
                </button>
              );
            }
            return (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-5 py-2.5 text-sm transition-colors ${
                    isActive ? "bg-primary text-white font-semibold" : "text-white/70 hover:bg-white/10 hover:text-white"
                  }`
                }
              >
                <Icon size={18} weight="bold" />
                {n.label}
                {n.to === "/work-queue" && urgent > 0 && (
                  <span className="ml-auto inline-flex items-center justify-center min-w-5 h-5 px-1.5 text-[11px] font-bold rounded-full bg-red-600 text-white" data-testid="wq-urgent-badge">{urgent}</span>
                )}
              </NavLink>
            );
          })}
        </nav>
        <button
          onClick={() => navigate("/change-password")}
          data-testid="change-password-link"
          className="flex items-center gap-3 px-5 py-3 text-sm text-white/70 hover:bg-white/10 hover:text-white border-t border-white/10"
        >
          <Lock size={18} weight="bold" /> Change password
        </button>
        <button
          onClick={logout}
          data-testid="logout-button"
          className="flex items-center gap-3 px-5 py-3 text-sm text-white/70 hover:bg-white/10 hover:text-white border-t border-white/10"
        >
          <SignOut size={18} weight="bold" /> Sign out
        </button>
        <button
          onClick={logoutAll}
          data-testid="logout-all-button"
          className="flex items-center gap-3 px-5 py-2.5 text-xs text-white/50 hover:bg-white/10 hover:text-white"
        >
          <Lock size={14} weight="bold" /> Log out from all devices
        </button>
      </aside>

      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-background border-b border-border flex items-center justify-between px-4 md:px-6 sticky top-0 z-20">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="menu-toggle">
            {open ? <X size={22} /> : <List size={22} />}
          </button>
          <div className="hidden md:block text-sm text-muted-foreground">
            {user?.role === "super_admin" ? "Platform Console" : "Collections Console"}
          </div>
          <div className="flex items-center gap-3">
            <SetupProgressChip />
            {["client_admin", "manager", "agent"].includes(user?.role) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="inline-flex items-center justify-center h-9 w-9 rounded-sm border border-border bg-card hover:bg-secondary transition-colors" title="Help" data-testid="help-menu-trigger">
                    <Question size={18} weight="bold" className="text-muted-foreground" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Help &amp; Onboarding</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => { if (window.location.pathname !== "/dashboard") navigate("/dashboard"); setTimeout(() => window.dispatchEvent(new Event("recoveros:start-quickstart")), 200); }}
                    data-testid="replay-quickstart-btn"
                  >
                    <Compass size={15} weight="bold" className="mr-2 text-[#0B3D91]" /> Replay RecoverOS Quick Start
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {isSuper && (
              <div className="flex items-center gap-2" data-testid="tenant-switcher">
                <Buildings size={18} weight="bold" className="text-primary hidden sm:block" />
                <Select value={activeTenant || "__all__"} onValueChange={selectTenant}>
                  <SelectTrigger className="rounded-sm h-9 w-44 md:w-56" data-testid="tenant-switcher-select">
                    <SelectValue placeholder="All Tenants" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__" data-testid="tenant-option-all">All Tenants</SelectItem>
                    {tenants.map((t) => (
                      <SelectItem key={t.tenant_id} value={t.tenant_id} data-testid={`tenant-option-${t.tenant_id}`}>{t.company_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="text-right">
              <div className="text-sm font-semibold leading-none">{user?.name}</div>
              <div className="label-caps mt-1">{roleLabel[user?.role]}</div>
            </div>
            {user?.picture ? (
              <img src={user.picture} alt="" className="h-9 w-9 rounded-sm object-cover border border-border" />
            ) : (
              <div className="h-9 w-9 rounded-sm bg-primary text-white flex items-center justify-center font-bold text-sm">
                {user?.name?.[0]?.toUpperCase()}
              </div>
            )}
          </div>
        </header>
        <TrialBanner />
        <DemoHelpBanner />
        <main className="flex-1 p-4 md:p-8 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
      <QuickStart />
      <InAppDemoFab />
      <SamplePortfolioExperience />
      <SampleInviteBanner />
      <ExitSurvey />
      <QuickAddAccountDialog open={quickAdd.open} onOpenChange={(o) => setQuickAdd((s) => ({ ...s, open: o }))} source={quickAdd.source} prefill={quickAdd.prefill} />
    </div>
  );
}
